// Small framer-motion helpers shared by the compare and compat screens.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export const spring = { type: 'spring' as const, stiffness: 420, damping: 36, mass: 0.9 };
export const pageVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
export const sectionItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: spring },
};

export function AnimatedSwap({
  value,
  className,
  as: Tag = 'span',
}: { value: React.ReactNode; className?: string; as?: React.ElementType }) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={typeof value === 'string' || typeof value === 'number' ? String(value) : 'swap'}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.18 }}
        className={className}
      >
        <Tag>{value}</Tag>
      </motion.span>
    </AnimatePresence>
  );
}

export function FlashHalo({
  children,
  depsKey,
  className,
  color = '42,164,166',
  intensity = 0.28,
  duration = 0.8,
}: {
  children: React.ReactNode;
  depsKey: React.Key;
  className?: string;
  color?: string;
  intensity?: number;
  duration?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [radius, setRadius] = useState<string>('12px');
  const [display, setDisplay] = useState<'block' | 'inline-block'>('inline-block');
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const child = (wrap.firstElementChild ?? wrap) as HTMLElement;
    const d = getComputedStyle(child).display;
    setDisplay(d === 'inline' || d === 'inline-block' || child.tagName === 'SPAN' ? 'inline-block' : 'block');
    const br = getComputedStyle(child).borderRadius;
    if (br && br !== '0px') setRadius(br);
    else {
      const rect = child.getBoundingClientRect();
      setRadius(rect.width > rect.height * 1.8 ? '9999px' : '12px');
    }
  }, [children]);
  return (
    <div ref={wrapRef} className={`relative isolate ${className ?? ''}`} style={{ display }}>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={String(depsKey)}
          initial={{ opacity: 0.9, scale: 0.985 }}
          animate={{ opacity: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration, ease: 'easeOut' }}
          className="pointer-events-none absolute -inset-1 will-change-[opacity,transform]"
          aria-hidden
          style={{
            borderRadius: radius,
            background: `radial-gradient(120% 120% at 50% 50%, rgba(${color}, ${intensity}) 0%, rgba(${color}, 0) 60%)`,
            filter: 'blur(12px)',
          }}
        />
      </AnimatePresence>
      {children}
    </div>
  );
}
